import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useSearchParams } from "react-router-dom"
import { useActiveClinic } from "../../context/ClinicContext"
import { useClinicPlanSnapshot } from "../../features/subscriptions/hooks/useClinicPlanSnapshot"
import { useClinicSubscription } from "../../features/subscriptions/hooks/useClinicSubscription"
import SettingsModuleScaffold from "./SettingsModuleScaffold"
import TreatmentRulesAdmin from "../../settings/TreatmentRulesAdmin"
import DoctorTreatmentMatrix from "../../settings/DoctorTreatmentMatrix"
import { DEFAULT_USER_ID } from "../../config/appConfig"
import {
  resolveClinicLabel,
  SectionCard,
  MetricCard,
  InsightCard,
  TopStateCard,
  actionButtonStyle
} from "./settingsShared"

const tabs = [
  { key: "rules", label: "Reglas clínicas" },
  { key: "matrix", label: "Matriz clínica" },
  { key: "odontogram", label: "Mapeo odontograma" }
]

export default function ClinicalEnginePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { activeClinicId } = useActiveClinic()

  const rawTab = searchParams.get("tab")
  const activeTab =
    rawTab === "matrix" || rawTab === "odontogram" || rawTab === "rules"
      ? rawTab
      : "rules"

  const clinicId =
    activeClinicId && activeClinicId !== "ALL" ? activeClinicId : null

  const userId =
    localStorage.getItem("odontoflow_user_id") || DEFAULT_USER_ID

  const [clinicRows, setClinicRows] = useState([])
  const [treatmentRows, setTreatmentRows] = useState([])
  const [doctorRows, setDoctorRows] = useState([])
  const [mappingRows, setMappingRows] = useState([])

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const { data: planSnapshot } = useClinicPlanSnapshot(clinicId)
  const { subscription } = useClinicSubscription(clinicId)

  const treatmentsCount = treatmentRows.length
  const doctorsCount = doctorRows.length
  const mappingsCount = mappingRows.length
  const clinicsCount = clinicRows.length

  const currentPlanName = planSnapshot?.plan?.name || "Sin plan activo"
  const accountName = planSnapshot?.accountName || "tu cuenta"

  const isReadOnly = Boolean(subscription?.access?.isReadOnly)
  const readOnlyReason =
    subscription?.access?.reason || "La clínica está en modo solo lectura."

  const activeClinicLabel = useMemo(
    () => resolveClinicLabel(clinicRows, clinicId),
    [clinicRows, clinicId]
  )

  const showActiveClinicMetric = clinicsCount > 1 && !!activeClinicLabel

  const handleTabChange = (tab) => {
    setSearchParams({ tab })
    setError("")
  }

  const loadClinics = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/multi-clinic/user-clinics", {
        params: { userId }
      })
      setClinicRows(Array.isArray(data) ? data : [])
    } catch {
      setClinicRows([])
    }
  }, [userId])

  const loadSnapshot = useCallback(async () => {
    if (!clinicId) {
      setTreatmentRows([])
      setDoctorRows([])
      setMappingRows([])
      return
    }

    setLoading(true)
    setError("")

    try {
      const [treatmentsRes, doctorsRes] = await Promise.allSettled([
        axios.get("/api/treatments", {
          params: { clinicId, limit: 300 }
        }),
        axios.get("/api/multi-clinic/clinic-doctors", {
          params: { clinicId }
        })
      ])

      if (treatmentsRes.status === "fulfilled") {
        setTreatmentRows(
          Array.isArray(treatmentsRes.value?.data) ? treatmentsRes.value.data : []
        )
      } else {
        setTreatmentRows([])
      }

      if (doctorsRes.status === "fulfilled") {
        setDoctorRows(
          Array.isArray(doctorsRes.value?.data) ? doctorsRes.value.data : []
        )
      } else {
        setDoctorRows([])
      }

      setMappingRows([])
    } catch (err) {
      const message =
        err?.response?.data?.message || "No se pudo cargar el motor clínico"
      setError(Array.isArray(message) ? message[0] : message)
    } finally {
      setLoading(false)
    }
  }, [clinicId])

  useEffect(() => {
    loadClinics()
    loadSnapshot()
  }, [loadClinics, loadSnapshot])

  const rulesStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail:
          "Debes seleccionar una clínica específica para trabajar reglas clínicas."
      }
    }

    if (treatmentsCount === 0) {
      return {
        label: "Pendiente",
        tone: "warning",
        detail:
          "Conviene cargar tratamientos antes de afinar reglas del motor clínico."
      }
    }

    return {
      label: "Configurable",
      tone: "info",
      detail:
        "Ya existe base clínica suficiente para definir reglas y comportamiento del motor."
    }
  }, [clinicId, treatmentsCount])

  const matrixStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail:
          "Debes seleccionar una clínica específica para trabajar la matriz clínica."
      }
    }

    if (doctorsCount === 0 || treatmentsCount === 0) {
      return {
        label: "Incompleto",
        tone: "warning",
        detail:
          "La matriz requiere tanto doctores como tratamientos para aportar valor real."
      }
    }

    return {
      label: "Operativo",
      tone: "success",
      detail:
        "La clínica ya tiene base suficiente para administrar su matriz operativa."
    }
  }, [clinicId, doctorsCount, treatmentsCount])

  const mappingsStatus = useMemo(() => {
    if (!clinicId) {
      return {
        label: "Selecciona clínica",
        tone: "warning",
        detail:
          "Debes seleccionar una clínica específica para administrar mapeos odontológicos."
      }
    }

    return {
      label: "Conexión pendiente",
      tone: "info",
      detail:
        "El mapeo automático del odontograma queda reservado para una siguiente etapa; por ahora no se consulta backend inexistente."
    }
  }, [clinicId])

  const actions = (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      <button
        onClick={() => handleTabChange("rules")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "rules" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "rules" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "rules"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver reglas
      </button>

      <button
        onClick={() => handleTabChange("matrix")}
        style={{
          ...actionButtonStyle,
          background: activeTab === "matrix" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "matrix" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "matrix"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver matriz
      </button>

      <button
        onClick={() => handleTabChange("odontogram")}
        style={{
          ...actionButtonStyle,
          background:
            activeTab === "odontogram" ? "#ffffff" : "rgba(255,255,255,0.08)",
          color: activeTab === "odontogram" ? "#0f172a" : "#ffffff",
          border:
            activeTab === "odontogram"
              ? "1px solid rgba(255,255,255,0.40)"
              : "1px solid rgba(255,255,255,0.16)"
        }}
      >
        Ver mapeos
      </button>
    </div>
  )

  return (
    <SettingsModuleScaffold
      eyebrow="Motor del sistema"
      title="Motor clínico"
      subtitle="Centraliza reglas, matriz operativa y mapeos odontológicos para que esta lógica avanzada deje de sentirse dispersa."
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      actions={actions}
    >
      {!clinicId ? (
        <TopStateCard tone="warning" title="Clínica no seleccionada">
          Selecciona una clínica específica para trabajar el motor clínico.
        </TopStateCard>
      ) : null}

      {isReadOnly ? (
        <TopStateCard tone="danger" title="Modo solo lectura">
          {readOnlyReason}
        </TopStateCard>
      ) : null}

      {error ? (
        <TopStateCard tone="danger" title="Error">
          {error}
        </TopStateCard>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 12
        }}
      >
        <MetricCard
          label="Tratamientos"
          value={loading ? "..." : String(treatmentsCount)}
          helper="Base clínica"
          tone={rulesStatus.tone}
        />

        <MetricCard
          label="Doctores"
          value={loading ? "..." : String(doctorsCount)}
          helper="Para matriz clínica"
          tone={matrixStatus.tone}
        />

        <MetricCard
          label="Mapeos"
          value={loading ? "..." : String(mappingsCount)}
          helper="Odontograma"
          tone={mappingsStatus.tone}
        />

        <MetricCard
          label="Plan"
          value={currentPlanName}
          helper={accountName}
          tone="info"
        />

        {showActiveClinicMetric ? (
          <MetricCard
            label="Clínica activa"
            value={activeClinicLabel}
            helper="Contexto actual"
            tone="neutral"
          />
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12
        }}
      >
        <InsightCard
          title="Reglas clínicas"
          status={rulesStatus.label}
          tone={rulesStatus.tone}
          detail={rulesStatus.detail}
          ctaLabel="Ir a reglas"
          onClick={() => handleTabChange("rules")}
        />

        <InsightCard
          title="Matriz clínica"
          status={matrixStatus.label}
          tone={matrixStatus.tone}
          detail={matrixStatus.detail}
          ctaLabel="Ir a matriz"
          onClick={() => handleTabChange("matrix")}
        />

        <InsightCard
          title="Mapeo odontograma"
          status={mappingsStatus.label}
          tone={mappingsStatus.tone}
          detail={mappingsStatus.detail}
          ctaLabel="Ir a mapeos"
          onClick={() => handleTabChange("odontogram")}
        />
      </div>

      {clinicId && activeTab === "rules" ? (
        <SectionCard
          title="Reglas clínicas"
          subtitle="Aquí se mantiene el módulo actual de reglas, ya agrupado dentro del motor clínico."
        >
          <TreatmentRulesAdmin />
        </SectionCard>
      ) : null}

      {clinicId && activeTab === "matrix" ? (
        <SectionCard
          title="Matriz clínica"
          subtitle="Aquí se conserva la matriz actual, ahora leída como parte del motor clínico y no como pantalla suelta."
        >
          <DoctorTreatmentMatrix />
        </SectionCard>
      ) : null}

      {clinicId && activeTab === "odontogram" ? (
        <SectionCard
          title="Mapeo odontograma"
          subtitle="El mapeo automático del odontograma queda marcado como conexión pendiente para no consultar endpoints que aún no existen en backend."
        >
          <TopStateCard tone="info" title="Conexión pendiente">
            Esta sección queda documentada dentro del motor clínico, pero no ejecuta llamadas a odontogram-mappings hasta que exista el módulo backend definitivo.
          </TopStateCard>
        </SectionCard>
      ) : null}
    </SettingsModuleScaffold>
  )
}
